import { useState } from "react";
import { Link, Twitter, Facebook, Mail, MessageCircle, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ShareBarProps {
  title: string;
  url?: string;
  className?: string;
  variant?: "horizontal" | "sticky-side";
}

const SHARE_PLATFORMS = [
  {
    id: "facebook",
    label: "Facebook",
    icon: Facebook,
    color: "hover:bg-blue-600 hover:text-white hover:border-blue-600",
    getUrl: (url: string, title: string) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: "twitter",
    label: "X / Twitter",
    icon: Twitter,
    color: "hover:bg-foreground hover:text-background hover:border-foreground",
    getUrl: (url: string, title: string) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: MessageCircle,
    color: "hover:bg-green-500 hover:text-white hover:border-green-500",
    getUrl: (url: string, title: string) =>
      `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  {
    id: "email",
    label: "Email",
    icon: Mail,
    color: "hover:bg-primary hover:text-primary-foreground hover:border-primary",
    getUrl: (url: string, title: string) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this article: ${url}`)}`,
  },
];

export const ShareBar = ({ title, url, className, variant = "horizontal" }: ShareBarProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: "Link copied!", description: "The link has been copied to your clipboard." });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: "Error", description: "Failed to copy the link.", variant: "destructive" });
    }
  };

  if (variant === "sticky-side") {
    return (
      <div className={cn("fixed left-4 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-2", className)}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-center mb-1 rotate-180 [writing-mode:vertical-rl]">
          Share
        </p>
        {SHARE_PLATFORMS.map((p) => (
          <a
            key={p.id}
            href={p.getUrl(shareUrl, title)}
            target="_blank"
            rel="noopener noreferrer"
            title={p.label}
            className={cn(
              "flex items-center justify-center h-10 w-10 rounded-xl border border-border bg-card text-muted-foreground transition-all duration-200 shadow-sm",
              p.color
            )}
          >
            <p.icon className="h-4 w-4" />
          </a>
        ))}
        <button
          onClick={handleCopy}
          title="Copy link"
          className={cn(
            "flex items-center justify-center h-10 w-10 rounded-xl border border-border bg-card text-muted-foreground transition-all duration-200 shadow-sm",
            copied
              ? "bg-green-500 text-white border-green-500"
              : "hover:bg-primary hover:text-primary-foreground hover:border-primary"
          )}
        >
          {copied ? <Check className="h-4 w-4" /> : <Link className="h-4 w-4" />}
        </button>
      </div>
    );
  }

  // Horizontal bar (default)
  return (
    <div className={cn("flex flex-wrap items-center gap-3 py-5 border-t border-b border-border my-10", className)}>
      <span className="text-sm font-bold uppercase tracking-widest text-muted-foreground mr-2">
        Share this article:
      </span>
      <div className="flex flex-wrap gap-2">
        {SHARE_PLATFORMS.map((p) => (
          <a
            key={p.id}
            href={p.getUrl(shareUrl, title)}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground bg-card transition-all duration-200 hover:shadow-sm",
              p.color
            )}
          >
            <p.icon className="h-4 w-4" />
            {p.label}
          </a>
        ))}
        <button
          onClick={handleCopy}
          className={cn(
            "inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium transition-all duration-200 hover:shadow-sm",
            copied
              ? "bg-green-500 text-white border-green-500"
              : "text-muted-foreground bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary"
          )}
        >
          {copied ? <Check className="h-4 w-4" /> : <Link className="h-4 w-4" />}
          {copied ? "Copied!" : "Copy link"}
        </button>
      </div>
    </div>
  );
};
